import { useState, useEffect, useCallback } from "react";
import { Loader2, Download, ArrowRight, Eye, EyeOff, Check } from "lucide-react";
import { toast } from "sonner";
import { useSession } from "@/context/SessionContext";
import SpaceApp from "./SpaceApp";
import "./space.css";
import "./playspace.css";

const LOGO = "/assets/space/mist_logo.jpg";

// Swap the document manifest + theme to the MiST game while this locked route
// is mounted, so an install here creates a SEPARATE, game-only home-screen icon
// (fullscreen, scoped to /play/space — no route back to the main Misteck site).
function useGameManifest() {
  useEffect(() => {
    const head = document.head;
    const link = head.querySelector('link[rel="manifest"]');
    const prevHref = link ? link.getAttribute("href") : null;
    if (link) link.setAttribute("href", "/space-manifest.json");

    const theme = head.querySelector('meta[name="theme-color"]');
    const prevTheme = theme ? theme.getAttribute("content") : null;
    if (theme) theme.setAttribute("content", "#050507");

    const appleTitle = head.querySelector('meta[name="apple-mobile-web-app-title"]');
    const prevAppleTitle = appleTitle ? appleTitle.getAttribute("content") : null;
    if (appleTitle) appleTitle.setAttribute("content", "MiST");

    const appleIcon = head.querySelector('link[rel="apple-touch-icon"]');
    const prevAppleIcon = appleIcon ? appleIcon.getAttribute("href") : null;
    if (appleIcon) appleIcon.setAttribute("href", "/space-icon-192.png");

    return () => {
      if (link && prevHref) link.setAttribute("href", prevHref);
      if (theme && prevTheme) theme.setAttribute("content", prevTheme);
      if (appleTitle && prevAppleTitle) appleTitle.setAttribute("content", prevAppleTitle);
      if (appleIcon && prevAppleIcon) appleIcon.setAttribute("href", prevAppleIcon);
    };
  }, []);
}

// Capture the deferred install prompt so we can surface a first-party
// "Install MiST" button instead of relying on the browser's mini-infobar.
function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(
    () => window.matchMedia("(display-mode: fullscreen)").matches ||
          window.matchMedia("(display-mode: standalone)").matches ||
          window.navigator.standalone === true
  );

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* dismissed */ }
    setDeferred(null);
  }, [deferred]);

  return { canInstall: !!deferred && !installed, installed, install };
}

/* ── Inline game-account auth (reuses the platform JWT) ───────────────────── */
function GameAuth() {
  const { signIn, signUp, verifyCode } = useSession();
  const [mode, setMode] = useState("signin"); // signin · signup · code
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const [pending, setPending] = useState(null); // email awaiting verification
  const [form, setForm] = useState({
    name: "", email: "", password: "", age_text: "", birth_date: "", code: "", agreed: false,
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const doSignin = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return;
    setBusy(true);
    try {
      await signIn(form.email, form.password);
      toast.success("Signed in — launching MiST");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
      setBusy(false);
    }
  };

  const doSignup = async (e) => {
    e.preventDefault();
    const { name, email, password, age_text, birth_date, agreed } = form;
    if (!name || !email || !password || !age_text || !birth_date) return toast.error("All fields are required");
    if (!agreed) return toast.error("Please accept the Terms & Privacy Policy");
    setBusy(true);
    try {
      const data = await signUp({ name, email, password, age_text, birth_date, accepted_terms: true });
      setPending(data.email);
      setMode("code");
      toast.success("Verification code dispatched");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setBusy(false);
    }
  };

  const doVerify = async (e) => {
    e.preventDefault();
    if (!form.code) return;
    setBusy(true);
    try {
      await verifyCode(pending, form.code);
      toast.success("Account verified — launching MiST");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
      setBusy(false);
    }
  };

  return (
    <div className="ps-auth" data-testid="playspace-auth">
      <img src={LOGO} alt="MiST" className="ps-auth-logo" />
      <p className="ps-auth-kicker">The Rise of Civilisation</p>
      <h1 className="ps-auth-title">
        {mode === "signup" ? "Create Game Account" : mode === "code" ? "Verify Account" : "Commander Login"}
      </h1>

      {mode !== "code" && (
        <div className="ps-tabs" data-testid="playspace-auth-tabs">
          <button className={mode === "signin" ? "on" : ""} onClick={() => setMode("signin")} data-testid="playspace-tab-signin">Sign In</button>
          <button className={mode === "signup" ? "on" : ""} onClick={() => setMode("signup")} data-testid="playspace-tab-signup">Sign Up</button>
        </div>
      )}

      {mode === "signin" && (
        <form onSubmit={doSignin} className="ps-form">
          <input className="ps-input" data-testid="playspace-signin-email" type="text" inputMode="email"
            autoCapitalize="none" autoCorrect="off" placeholder="Username or email"
            value={form.email} onChange={set("email")} />
          <div className="ps-pw">
            <input className="ps-input" data-testid="playspace-signin-password" type={show ? "text" : "password"}
              placeholder="Password" value={form.password} onChange={set("password")} />
            <button type="button" onClick={() => setShow((s) => !s)} className="ps-eye" aria-label="Toggle password">
              {show ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>
          <button className="ps-cta" data-testid="playspace-signin-submit" disabled={busy}>
            {busy ? <Loader2 size={17} className="ps-spin" /> : <>Enter Command <ArrowRight size={16} /></>}
          </button>
        </form>
      )}

      {mode === "signup" && (
        <form onSubmit={doSignup} className="ps-form">
          <input className="ps-input" data-testid="playspace-signup-name" placeholder="Commander name" value={form.name} onChange={set("name")} />
          <input className="ps-input" data-testid="playspace-signup-email" type="email" placeholder="Email" value={form.email} onChange={set("email")} />
          <input className="ps-input" data-testid="playspace-signup-password" type="password" placeholder="Password (min 6 chars)" value={form.password} onChange={set("password")} />
          <div className="ps-row">
            <input className="ps-input" data-testid="playspace-signup-age" inputMode="numeric" placeholder="Age" value={form.age_text} onChange={set("age_text")} />
            <input className="ps-input" data-testid="playspace-signup-dob" type="date" value={form.birth_date} onChange={set("birth_date")} />
          </div>
          <button type="button" data-testid="playspace-signup-terms" className="ps-terms" onClick={() => setForm((f) => ({ ...f, agreed: !f.agreed }))}>
            <span className={`ps-check ${form.agreed ? "on" : ""}`}>{form.agreed && <Check size={12} strokeWidth={4} />}</span>
            I agree to the Terms of Service & Privacy Policy.
          </button>
          <button className="ps-cta" data-testid="playspace-signup-submit" disabled={busy || !form.agreed}>
            {busy ? <Loader2 size={17} className="ps-spin" /> : <>Create & Continue <ArrowRight size={16} /></>}
          </button>
        </form>
      )}

      {mode === "code" && (
        <form onSubmit={doVerify} className="ps-form">
          <p className="ps-hint">Enter the 6-digit code sent to {pending}.</p>
          <input className="ps-input ps-code" data-testid="playspace-verify-code" inputMode="numeric" maxLength={6}
            placeholder="••••••" value={form.code} onChange={set("code")} />
          <button className="ps-cta" data-testid="playspace-verify-submit" disabled={busy}>
            {busy ? <Loader2 size={17} className="ps-spin" /> : <>Verify & Launch <ArrowRight size={16} /></>}
          </button>
          <button type="button" className="ps-link" onClick={() => setMode("signin")}>Back to sign in</button>
        </form>
      )}
    </div>
  );
}

// Dedicated, LOCKED MiST route. Renders the game in a chrome-free 9:16 stage with
// NO Misteck navigation — the only exit is closing the app/window. Reachable via
// the /play/space URL (web-search discovery) and installable as its own PWA icon.
export default function PlaySpace() {
  const { authed } = useSession();
  useGameManifest();
  const { canInstall, install } = useInstallPrompt();

  return (
    <div className="ps-shell" data-testid="playspace-shell">
      <div className="ps-stage" data-testid="playspace-stage">
        {authed === null ? (
          <div className="ps-boot"><Loader2 size={26} className="ps-spin" /><span>Booting MiST…</span></div>
        ) : authed === true ? (
          <SpaceApp onBack={() => {}} />
        ) : (
          <GameAuth />
        )}
      </div>

      {canInstall && (
        <button className="ps-install" data-testid="playspace-install-btn" onClick={install}>
          <Download size={15} /> Install MiST
        </button>
      )}
    </div>
  );
}
